import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Card, Checkbox, Combobox } from '@/components/ui'
import { CheckCircle, Clock, Loader2 } from 'lucide-react'
import Loader from '../../../common/components/Loader'
import {
  ShippingBand,
  ShippingBandPayload,
  ShippingStatus,
} from '../../../common/models/shipping-bands'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDate } from '../../../core/constants'
import { useTableParams } from '../../../core/hooks/table-params'
import {
  downloadAwb,
  getShippings,
  updateShipping,
} from '../../../core/services/shippings'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { ViewShippingBand } from './View'
import { cn } from '@/lib/utils'

export const ShippingBandsPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    sortBy,
    setSortBy,
    totalElements,
    setTotalElemenets: setTotalElements,
  } = useTableParams()
  const { festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [item, setItem] = useState<ShippingBand>()

  const options = [
    { value: undefined, label: 'View All' },
    { value: true, label: 'Ready' },
    { value: false, label: 'Not Ready' },
  ]

  const [items, setItems] = useState<ShippingBand[]>([])

  const [loading, setLoading] = useState(true)
  const [show, setShow] = useState(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [selectedFilter, setSelectedFilter] = useState<{
    label: string
    value: boolean | undefined
  }>(options[0])
  const [updatingPackages, setUpdatingPackages] = useState<Set<string>>(
    new Set(),
  )

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const { data, status } = await getShippings({
        page,
        size,
        sortBy,
        sortDirection,
        packageReady: selectedFilter?.value,
      })
      if (status === 200) {
        setItems(data.content)
        setTotalElements(data.totalElements)
        setSize(data.size)
        setPage(data.pageNumber + 1)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [page, size, sortBy, sortDirection, selectedFilter, setPage, setSize])

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  useEffect(() => {
    getData()
  }, [getData, page, size])

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const handleReadyPackage = async (
    id: string,
    statusProp: ShippingStatus,
    packageReady: boolean,
  ) => {
    try {
      setUpdatingPackages((prev) => new Set(prev).add(id))
      const payload: ShippingBandPayload = {
        status: statusProp,
        packageReady: !packageReady,
      }
      const resp = await updateShipping(id, payload)
      if (resp.status === 200) {
        setTimeout(() => {
          getData()
        }, 2000)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setUpdatingPackages((prev) => {
        const newSet = new Set(prev)
        newSet.delete(id)
        return newSet
      })
    }
  }

  const columns: IColumn[] = [
    {
      title: 'Id',
      accessor: 'id',
    },
    {
      title: 'Ticket Name',
      accessor: 'ticketName',
    },
    {
      title: 'Barcode',
      accessor: 'barcode',
    },
    {
      title: 'Buyer',
      accessor: '',
      render: (row) => (
        <div className="flex flex-col gap-1">
          <span>{row.buyer.name}</span>
          <span>{row.buyer.phone}</span>
          <span>{row.buyer.email}</span>
        </div>
      ),
    },
    {
      title: 'Check-in Info',
      accessor: '',
      render: (row) => (
        <div className="flex flex-col gap-1">
          <span>
            {`${row.shippingCheckinInfo?.firstName} ${row.shippingCheckinInfo.lastName}` ||
              '-'}
          </span>
          <span>{row.shippingCheckinInfo?.phoneNumber || '-'}</span>
          <span>{row.shippingCheckinInfo?.email || '-'}</span>
        </div>
      ),
    },
    {
      title: 'Shipping Address',
      accessor: '',
      render: (row) => (
        <div className="flex flex-col gap-1">
          <span>{`${row.shippingAddress.address}`}</span>
          <span>{`${row.shippingAddress.countryIso3Code}, ${row.shippingAddress.city}`}</span>
          <span>{`${row.shippingAddress.postCode}`}</span>
        </div>
      ),
    },
    {
      title: 'Last Update',
      accessor: 'lastUpdate',
      render: (row) => <span>{formatDate(row.lastUpdate)}</span>,
    },
    {
      title: 'Status',
      accessor: '',
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.status.name === 'SENT' ? (
            <CheckCircle
              className={cn(
                row.status.name === 'SENT' ? 'text-accent-1' : 'text-accent-4',
              )}
            />
          ) : (
            <Clock
              className={cn(
                row.status.name === 'IN_PROCESS'
                  ? 'text-blue-500'
                  : 'text-accent-4',
              )}
            />
          )}
          <span className="text-monochrome-1">{row.status.displayValue}</span>
        </div>
      ),
    },
    {
      title: 'Package Ready',
      accessor: 'packageReady',
      sortable: true,
      render: (row, index) => {
        const isUpdating = updatingPackages.has(items[index].id)
        if (isUpdating) {
          return <Loader2 className="h-4 w-4 animate-spin" />
        }
        return (
          <Checkbox
            disabled={row.packageReady || isUpdating}
            checked={row.packageReady}
            onCheckedChange={() =>
              handleReadyPackage(items[index].id, row.status, row.packageReady)
            }
          />
        )
      },
    },
    {
      title: 'AWB Code',
      accessor: 'awbCode',
    },
    {
      title: 'Print',
      accessor: '',
      render: (row) =>
        row.awbCode ? (
          <a
            className="view text-accent-1 underline cursor-pointer"
            onClick={async () => {
              try {
                const response = await downloadAwb(row.awbCode)

                const href = URL.createObjectURL(response.data)

                // create "a" HTML element with href to file & click
                const link = document.createElement('a')
                link.href = href
                link.setAttribute('download', `${row.awbCode}.pdf`) //or any other extension
                document.body.appendChild(link)
                link.click()

                // clean up "a" element & remove ObjectURL
                document.body.removeChild(link)
                URL.revokeObjectURL(href)
              } catch (err) {
                console.log(err)
              }
            }}
          >
            Print
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: 'View',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline cursor-pointer"
          onClick={async () => {
            setItem(row)
            setShow(true)
          }}
        >
          View
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Shipping Bands"
        actions={
          <div className="flex items-center gap-2">
            <span className="text-monochrome-1">Package Ready</span>
            <Combobox
              options={options.map((opt) => ({
                label: opt.label,
                value: opt.value?.toString() || 'undefined',
              }))}
              value={selectedFilter?.value?.toString() || 'undefined'}
              onValueChange={(value) => {
                const option = options.find(
                  (opt) => (opt.value?.toString() || 'undefined') === value,
                )
                if (option) {
                  setSelectedFilter(option)
                }
              }}
              placeholder="Select Package Status"
              className="w-[200px]"
            />
          </div>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Shipping Bands" className="mb-5" />
        <Table
          className="[&_td]:!whitespace-nowrap"
          handleSortBy={handleSortBy}
          columns={columns}
          data={items}
          columnWidth="15%"
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <ViewShippingBand
        visible={show}
        setVisible={() => setShow(false)}
        setItems={setItems}
        item={item}
        getData={getData}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
